import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type { MessageKey } from './en';
import { isLocaleId, type LocaleId } from './locales';
import { translate, type TranslateVars } from './translate';

const STORAGE_KEY = 'mockloc-locale';

export type Translate = (key: MessageKey, vars?: TranslateVars) => string;

export interface LanguageContextValue {
  locale: LocaleId;
  setLocale: (locale: LocaleId) => void;
  t: Translate;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function readStoredLocale(): LocaleId {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isLocaleId(stored)) return stored;
  } catch {
    /* Private mode can block storage. Fall through to English (United States). */
  }
  return 'en-US';
}

function storeLocale(locale: LocaleId): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* Ignore storage failures. The in-memory choice still applies. */
  }
}

function applyLocale(locale: LocaleId): void {
  document.documentElement.lang = locale;
}

const fallbackT: Translate = (key, vars) => translate('en-US', key, vars);

const FALLBACK: LanguageContextValue = {
  locale: 'en-US',
  setLocale: () => undefined,
  t: fallbackT,
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<LocaleId>(() => readStoredLocale());

  useLayoutEffect(() => {
    applyLocale(locale);
  }, [locale]);

  const setLocale = useCallback((next: LocaleId) => {
    if (!isLocaleId(next)) return;
    storeLocale(next);
    setLocaleState(next);
  }, []);

  const t = useCallback<Translate>((key, vars) => translate(locale, key, vars), [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext) ?? FALLBACK;
}
